import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import ProjectCard from './ProjectCard';
import FilterTabs from './FilterTabs';
import { fetchPortfolioData } from '../utils/dataFetcher';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle } = FiIcons;

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchPortfolioData();
        setProjects(data);
        setFilteredProjects(data);
        const uniqueCategories = ['All', ...new Set(data.map(project => project.cat).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (err) {
        setError('Failed to load portfolio data');
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.cat === activeCategory));
    }
  }, [activeCategory, projects]);

  const handleProjectClick = (project, index) => {
    navigate(`/project/${index}`, { state: { project, projects } });
  };

  const handleQuoteClick = () => {
    window.open('https://angrybranding.com/#request-a-quote', '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 sm:w-12 sm:h-12 border-4 border-orange-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center px-4">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Header />
      
      <main className="px-3 sm:px-4 py-4 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 className={`text-3xl sm:text-5xl md:text-7xl font-black mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Our <span className="text-orange-500">Portfolio</span>
          </h1>
          <p className={`text-base sm:text-xl md:text-2xl max-w-3xl mx-auto mb-6 sm:mb-8 px-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Showcasing exceptional digital experiences that push boundaries and deliver results
          </p>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleQuoteClick}
            className="inline-flex items-center space-x-2 sm:space-x-3 px-4 sm:px-8 py-3 sm:py-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-semibold text-sm sm:text-lg shadow-lg shadow-orange-500/25"
          >
            <SafeIcon icon={FiMessageCircle} className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Start Your Project</span>
          </motion.button>
        </motion.div>

        <FilterTabs
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mt-8 sm:mt-12"
        >
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              index={index}
              onClick={() => handleProjectClick(project, index)}
            />
          ))}
        </motion.div>

        {filteredProjects.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 sm:py-20"
          >
            <p className={`text-lg sm:text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              No projects found in this category
            </p>
          </motion.div>
        )}
      </main>

      <footer className={`py-4 sm:py-8 border-t ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className="px-3 sm:px-4 text-center">
          <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            © 2025 Powered by <span className="text-orange-500 font-bold">Angry Branding</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;